/**
 * Okendo reviews for the index Reviews section.
 *
 * One custom element, <lp-okendo-reviews>, does everything for the section:
 * it reads its settings from the JSON script inside it, fetches live reviews
 * from the Okendo Storefront REST API, filters them, builds the slides, and
 * runs the one-review-at-a-time carousel (arrows, dots, keyboard, swipe).
 *
 * The section renders its static review blocks inside the track first. They
 * are the fallback: if live reviews are switched off, the Okendo User ID is
 * missing, the request fails, or nothing passes the filters, the static
 * slides stay and the carousel runs on those instead.
 *
 * API notes (https://docs.okendo.io, Storefront REST API):
 * - Public, no key. The only identifier is the store's Okendo User ID.
 * - Max 25 reviews per request; more come through `nextUrl`.
 * - The `okendo-api-version` header is NOT sent. Okendo's CORS preflight does
 *   not allow it, so a browser request carrying it is blocked. Without it the
 *   API answers with its default version.
 * - The API cannot filter by rating, so reviews are sorted by `orderBy` and
 *   filtered here.
 * - Customer uploads come in `media`. Image entries carry a 540x720 portrait
 *   crop (`largePortraitThumbnailUrl`) whose size is set by its `d` parameter.
 *
 * Review text is always written with textContent, never as HTML.
 */

const API_ROOT = 'https://api.okendo.io/v1';
const PAGE_SIZE = 25;
const MAX_PAGES = 3;
const FETCH_TIMEOUT_MS = 6000;
const CACHE_MINUTES = 10;
const SWIPE_DISTANCE = 50;
const PHOTO_WIDTHS = [360, 540, 720];
const PHOTO_SIZES = '(min-width: 1024px) 320px, 220px';

/**
 * @typedef {Object} OkendoMedia
 * @property {string} type
 * @property {string} [alt]
 * @property {string} [largePortraitThumbnailUrl]
 */

/**
 * @typedef {Object} OkendoReview
 * @property {string} body
 * @property {number} rating
 * @property {string} [productName]
 * @property {string} [productHandle]
 * @property {string} [status]
 * @property {OkendoMedia[]} [media]
 * @property {{ displayName?: string, isVerified?: boolean }} [reviewer]
 */

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 * @returns {HTMLElement}
 */
const createElement = (tag, className, text) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
};

export class LpOkendoReviews extends HTMLElement {
  index = 0;
  slides = [];
  touchStartX = 0;
  touchStartY = 0;
  abortController = undefined;

  connectedCallback() {
    this.config = this.readConfig();
    this.track = this.querySelector('[data-track]');
    if (!this.config || !this.track) return;

    this.dotsContainer = this.querySelector('[data-dots]');
    this.arrows = this.querySelector('[data-arrows]');
    this.starTemplate = this.querySelector('template[data-star]');
    this.badgeTemplate = this.querySelector('template[data-badge-icon]');

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });

    this.init();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchend', this.onTouchEnd);
  }

  /** @returns {Object|null} */
  readConfig() {
    const script = this.querySelector('script[data-config]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Okendo reviews: invalid config', error);
      return null;
    }
  }

  /**
   * Loads live reviews when they are switched on, otherwise runs on the static ones.
   * @returns {Promise<void>}
   */
  async init() {
    const useLive = this.config.live && this.config.userId;
    if (!useLive) {
      this.useStaticSlides('static');
      return;
    }

    this.setAttribute('aria-busy', 'true');

    try {
      const reviews = await this.loadReviews();
      if (reviews.length > 0) {
        this.renderLiveSlides(reviews);
        this.finish('live');
        return;
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Okendo reviews: using fallback reviews', error);
    }

    this.useStaticSlides('fallback');
  }

  /**
   * @param {string} state
   */
  useStaticSlides(state) {
    this.slides = Array.from(this.track.querySelectorAll('.lp-index-reviews__slide'));

    if (this.slides.length === 0) {
      this.finish('empty');
      this.closest('.lp-index-reviews')?.setAttribute('hidden', '');
      return;
    }

    this.labelSlides();
    this.finish(state);
  }

  /**
   * @param {string} state
   */
  finish(state) {
    this.dataset.state = state;
    this.removeAttribute('aria-busy');
    this.buildDots();
    this.updateControls();
    this.go(0);
    // Announce slide changes only after the first render, so loading is silent.
    this.track.setAttribute('aria-live', 'polite');
  }

  /* ----- Fetching ----- */

  /** @returns {Promise<OkendoReview[]>} */
  async loadReviews() {
    const cacheKey = this.cacheKey();
    const cached = this.readCache(cacheKey);
    if (cached) return cached;

    this.abortController = new AbortController();
    const timeout = setTimeout(() => this.abortController.abort(), FETCH_TIMEOUT_MS);

    try {
      const reviews = await this.fetchReviews(this.abortController.signal);
      this.writeCache(cacheKey, reviews);
      return reviews;
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<OkendoReview[]>}
   */
  async fetchReviews(signal) {
    const { userId, orderBy, limit } = this.config;
    const collected = [];
    const firstUrl = new URL(`${API_ROOT}/stores/${encodeURIComponent(userId)}/reviews`);
    firstUrl.searchParams.set('limit', String(PAGE_SIZE));
    firstUrl.searchParams.set('orderBy', orderBy);

    let nextUrl = firstUrl.toString();

    for (let page = 0; page < MAX_PAGES && nextUrl && collected.length < limit; page++) {
      const response = await fetch(nextUrl, { signal });
      if (!response.ok) throw new Error(`Okendo request failed with status ${response.status}`);

      const data = await response.json();
      for (const review of data.reviews ?? []) {
        if (collected.length >= limit) break;
        if (this.isEligible(review)) collected.push(review);
      }

      nextUrl = data.nextUrl ? `${API_ROOT}${data.nextUrl}` : '';
    }

    return collected;
  }

  /**
   * @param {OkendoReview} review
   * @returns {boolean}
   */
  isEligible(review) {
    const body = typeof review.body === 'string' ? review.body.trim() : '';
    if (!body || body.length < this.config.minLength) return false;
    if (review.rating < this.config.minRating) return false;
    if (this.config.verifiedOnly && !review.reviewer?.isVerified) return false;
    if (review.status && review.status !== 'approved') return false;
    return true;
  }

  cacheKey() {
    const { userId, orderBy, limit, minRating, minLength, verifiedOnly } = this.config;
    return `lp-okendo-reviews:${[userId, orderBy, limit, minRating, minLength, verifiedOnly].join('|')}`;
  }

  /**
   * @param {string} key
   * @returns {OkendoReview[]|null}
   */
  readCache(key) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(key));
      if (cached && Date.now() - cached.time < CACHE_MINUTES * 60000) return cached.reviews;
    } catch (error) {
      // Storage can be blocked or hold bad data; fetching again is fine.
    }
    return null;
  }

  /**
   * @param {string} key
   * @param {OkendoReview[]} reviews
   */
  writeCache(key, reviews) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), reviews }));
    } catch (error) {
      // Storage can be full or blocked; the feed still works without it.
    }
  }

  /* ----- Building slides ----- */

  /**
   * @param {OkendoReview[]} reviews
   */
  renderLiveSlides(reviews) {
    this.track.replaceChildren(...reviews.map((review) => this.buildSlide(review)));
    this.slides = Array.from(this.track.querySelectorAll('.lp-index-reviews__slide'));
    this.labelSlides();
  }

  /**
   * @param {OkendoReview} review
   * @returns {HTMLElement}
   */
  buildSlide(review) {
    const slide = createElement('article', 'lp-index-reviews__slide');
    const content = createElement('div', 'lp-index-reviews__content');
    const photo = this.config.showPhoto ? this.findPhoto(review) : null;

    if (photo) {
      slide.classList.add('lp-index-reviews__slide--has-photo');
      slide.append(this.buildPhoto(photo, review));
    }

    if (this.config.showStars) content.append(this.buildStars(review.rating));

    const quote = createElement('blockquote', 'lp-index-reviews__quote', `“${this.truncate(review.body.trim())}”`);
    content.append(quote);
    content.append(this.buildAuthor(review));
    slide.append(content);

    return slide;
  }

  /**
   * The first uploaded image; videos are skipped.
   * @param {OkendoReview} review
   * @returns {OkendoMedia|undefined}
   */
  findPhoto(review) {
    return review.media?.find((item) => item.type === 'image' && item.largePortraitThumbnailUrl);
  }

  /**
   * @param {OkendoMedia} photo
   * @param {OkendoReview} review
   * @returns {HTMLElement}
   */
  buildPhoto(photo, review) {
    const wrapper = createElement('div', 'lp-index-reviews__photo');
    const image = createElement('img', 'lp-index-reviews__photo-image');
    const name = review.reviewer?.displayName;

    image.src = photo.largePortraitThumbnailUrl;
    image.srcset = PHOTO_WIDTHS.map((width) => `${this.resizePhoto(photo.largePortraitThumbnailUrl, width)} ${width}w`).join(', ');
    image.sizes = PHOTO_SIZES;
    image.width = 540;
    image.height = 720;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.alt = photo.alt || (name ? this.config.strings.photo.replace('__NAME__', name) : this.config.strings.photoAnonymous);

    wrapper.append(image);
    return wrapper;
  }

  /**
   * Okendo sizes its images from the `d` parameter; keeps the 3:4 crop.
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  resizePhoto(url, width) {
    const resized = new URL(url);
    resized.searchParams.set('d', `${width}x${Math.round((width * 4) / 3)}`);
    return resized.toString();
  }

  /**
   * @param {number} rating
   * @returns {HTMLElement}
   */
  buildStars(rating) {
    const stars = createElement('div', 'lp-index-reviews__rating');
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', this.config.strings.rating.replace('__RATING__', String(rating)));

    for (let i = 0; i < rating; i++) {
      const star = createElement('span', 'lp-index-reviews__star');
      star.setAttribute('aria-hidden', 'true');
      if (this.starTemplate) star.append(this.starTemplate.content.cloneNode(true));
      stars.append(star);
    }

    return stars;
  }

  /**
   * @param {OkendoReview} review
   * @returns {HTMLElement}
   */
  buildAuthor(review) {
    const author = createElement('div', 'lp-index-reviews__author');
    const name = review.reviewer?.displayName;

    if (name) author.append(createElement('span', 'lp-index-reviews__author-name', name));

    if (this.config.showVerified && review.reviewer?.isVerified) {
      author.append(this.buildBadge());
    }

    if (this.config.showProduct && review.productName && review.productHandle) {
      const link = createElement('a', 'lp-index-reviews__product', review.productName);
      const rootUrl = (this.config.rootUrl || '').replace(/\/$/, '');
      link.href = `${rootUrl}/products/${encodeURIComponent(review.productHandle)}`;
      author.append(link);
    }

    return author;
  }

  /** @returns {HTMLElement} */
  buildBadge() {
    const badge = createElement('span', 'lp-index-reviews__badge');
    if (this.config.badgeFilled) badge.classList.add('lp-index-reviews__badge--filled');
    if (this.badgeTemplate) badge.append(this.badgeTemplate.content.cloneNode(true));
    badge.append(document.createTextNode(this.config.verifiedLabel));
    return badge;
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  truncate(text) {
    const max = this.config.maxLength;
    if (!max || text.length <= max) return text;

    const cut = text.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    const trimmed = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
    return `${trimmed.trimEnd()}…`;
  }

  /** Gives every slide (live or static) the carousel slide semantics. */
  labelSlides() {
    const total = this.slides.length;

    for (const [i, slide] of this.slides.entries()) {
      const label = this.config.strings.slide.replace('__CURRENT__', String(i + 1)).replace('__TOTAL__', String(total));
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', this.config.strings.slideName);
      slide.setAttribute('aria-label', label);
    }
  }

  /* ----- Carousel ----- */

  /** Builds one labelled button per slide, or hides the dots when there is nothing to page through. */
  buildDots() {
    if (!this.dotsContainer) return;

    const total = this.slides.length;
    const buttons = [];

    if (this.config.showDots && total > 1) {
      for (let i = 0; i < total; i++) {
        const dot = createElement('button', 'lp-index-reviews__dot');
        dot.type = 'button';
        dot.dataset.index = String(i);
        dot.setAttribute('aria-label', this.config.strings.goTo.replace('__NUMBER__', String(i + 1)));
        buttons.push(dot);
      }
    }

    this.dotsContainer.replaceChildren(...buttons);
    this.dotsContainer.hidden = buttons.length === 0;
  }

  /** Hides the arrows when there is only one slide. */
  updateControls() {
    if (this.arrows) this.arrows.hidden = this.slides.length < 2;
  }

  /**
   * @param {number} next
   */
  go(next) {
    const total = this.slides.length;
    if (total === 0) return;

    this.index = (next + total) % total;
    this.track.style.transform = `translateX(${-this.index * 100}%)`;

    for (const [i, slide] of this.slides.entries()) {
      slide.inert = i !== this.index;
    }

    const dots = this.dotsContainer ? this.dotsContainer.querySelectorAll('.lp-index-reviews__dot') : [];
    for (const [i, dot] of Array.from(dots).entries()) {
      const isActive = i === this.index;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }

  /* ----- Events ----- */

  /**
   * Handles the arrow and dot buttons.
   * @param {MouseEvent} event
   */
  onClick = (event) => {
    const control = event.target.closest('[data-dir], .lp-index-reviews__dot');
    if (!control || !this.contains(control)) return;

    if (control.dataset.dir) {
      this.go(this.index + (control.dataset.dir === 'next' ? 1 : -1));
    } else {
      this.go(Number(control.dataset.index));
    }
  };

  /**
   * Left and right arrow keys move between reviews.
   * @param {KeyboardEvent} event
   */
  onKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
  };

  /**
   * @param {TouchEvent} event
   */
  onTouchStart = (event) => {
    if (!event.touches.length) return;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  };

  /**
   * A horizontal swipe longer than SWIPE_DISTANCE moves to the next or previous review.
   * @param {TouchEvent} event
   */
  onTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    if (Math.abs(deltaX) < SWIPE_DISTANCE || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    this.go(this.index + (deltaX < 0 ? 1 : -1));
  };
}

if (!customElements.get('lp-okendo-reviews')) {
  customElements.define('lp-okendo-reviews', LpOkendoReviews);
}
